/**
 * Todo Progress Widget Container
 *
 * App-layer container that connects the presentational
 * {@link TodoProgressWidget} to the todo-list store.
 *
 * The container — not the page — hydrates the store. `DashboardPage` lives in
 * `features/dashboard` and may not import `features/todo-list` (the
 * `boundaries/element-types` rule forbids feature-to-feature imports), which is
 * exactly why the dashboard injects its widgets from the app layer. So the only
 * place allowed to trigger the todo load for `/dashboard` is here. Without it
 * the widget rendered whatever the store happened to hold — empty until the
 * user visited `/todo-list` — and the dashboard greeted every fresh sign-in
 * with „0 от 0 завършени" while the family had tasks.
 *
 * @module app/widgets/TodoProgressWidgetContainer
 */

import { useTodoStore } from '@/features/todo-list/stores/todoStore';
import { TodoProgressWidget } from '@/shared/components/widgets/TodoProgressWidget';
import { useFamilyScopedLoad } from '@/shared/hooks/useFamilyScopedLoad';

interface TodoProgressWidgetContainerProps {
  readonly pendingCount?: number;
  readonly className?: string;
  readonly showHeader?: boolean;
  readonly showPendingList?: boolean;
}

export function TodoProgressWidgetContainer(
  props: TodoProgressWidgetContainerProps
) {
  const tasks = useTodoStore((state) => state.tasks);
  const taskLists = useTodoStore((state) => state.taskLists);
  const toggleTaskStatus = useTodoStore((state) => state.toggleTaskStatus);
  const load = useTodoStore((state) => state.load);

  // Same hook `TodoListPage` uses: hydrate on mount and re-hydrate on every
  // family switch. `load()` is idempotent (guarded by `isLoaded` / `isLoading`)
  // so mounting alongside another consumer costs no extra request, and the
  // family-scoped reset clears `isLoaded` so the re-load actually re-reads.
  useFamilyScopedLoad(load);

  return (
    <TodoProgressWidget
      {...props}
      tasks={tasks}
      taskLists={taskLists}
      onToggleTask={toggleTaskStatus}
    />
  );
}
